import React, { useRef, useEffect, useState } from "react";
import { useTheme, useMediaQuery, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Container from "components/common/Container";
import Stack from "components/mui/Stack";
import Box from "components/mui/Box";
import Typography from "components/mui/Typography";
import { typography } from "theme";
import ROUTES from "router/routes.constants";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useInitiateKycMutation, useUpdateDigioStatusMutation } from "store/api/kyc.apislice";
import { useGetOnboardingStatusQuery } from "store/api/customer.apislice";
import { isDirectCustomer } from "utils/investmentEligibility.utils";
import ConfirmationModel from "../../components/common/modal/ConfirmationModel";
import{toast} from 'react-hot-toast';
// import { isNative } from "utils/platform.utils";
// import { Capacitor } from "@capacitor/core";

const VERIFICATION_STATUS = {
  PENDING: "PENDING",
  SUCCEED: "SUCCEED",
  FAILED: "FAILED",
  NOT_CONFIRMED: "NOT_CONFIRMED",
  CANCELLED: "CANCELLED",
};

const REDIRECTION_URIS = {
  SUCCEED: "/onboarding/status",
  FAILED: "/onboarding/status",
  CANCELLED: "/onboarding/status",
  NOT_CONFIRMED: "/onboarding/status",
};

const KYCContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const digioRef = useRef(null);
  const intervalRef = useRef(null);

  const panNo = location.state?.panNo;
  const dob = location.state?.dob;
  console.log('panNo', location);
  const kraDetails = location.state?.kraDetails;
  console.log('kraDetails', kraDetails);

  const [containerState, setContainerState] = useState({
    dialogContent: {
      title: "",
      content: "",
      actionText: "",
    },
    dialogOpen: false,
    isLoading: false,
    verificationStatus: "",
    isSigningInProcess: false,
  });
  // const [kycData, setKycData] = useState(null);
  const [initiateKyc] = useInitiateKycMutation();
  const [updateDigioStatus] = useUpdateDigioStatusMutation();
  const { data: onboardingData, refetch: refetchOnboardingStatus } =
    useGetOnboardingStatusQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });

  // DIRECT customers must complete KYC — hide the skip option for them
  const isDirect = isDirectCustomer?.(onboardingData ?? null) ?? false;
  const payload = {
    pan : panNo,
    dob : dob?.split('-').reverse().join('/'),
  };

  console.log('payload', payload);

  useEffect(() => {
    initiateKyc(payload).then((response) => {
      console.log("Initiate KYC response:", response);
      if (response?.data) {
        // setKycData(response?.data);
        initDigio({
          documentId: response?.data?.accessToken?.entityId|| response?.data?.workflowDetails?.accessToken?.entityId || kraDetails?.workflowDetails?.kid,
          accessToken: response?.data?.workflowDetails?.accessToken?.id || response?.data?.accessToken?.id || kraDetails?.workflowDetails?.accessToken?.id,
          identifier: response?.data?.workflowDetails?.customerIdentifier || response?.data?.customerIdentifier || kraDetails?.workflowDetails?.customerIdentifier,
        });
        // initDigio({
        //   documentId: response?.data?.id,
        //   accessToken: response?.data?.accessToken?.id,
        //   identifier: response?.data?.customerIdentifier,
        // });
      }
    });

    return () => {
      clearInterval(intervalRef.current);
      digioRef.current = null;
      // setKycData(null);
      setContainerState({
        dialogContent: {
          title: "",
          content: "",
          actionText: "",
        },
        dialogOpen: false,
      });
    }
  }, []);

  const initDigio = async ({ documentId, accessToken, identifier }) => {
    // const isNative = () => Capacitor.isNativePlatform()
    try {
      digioRef.current = new window.Digio({
        // environment: 'sandbox',
        environment: 'production',
        // logo: "https://inxits.com/wp-content/themes/inxit-theme/assets/img/logo.svg",
        // theme: { primaryColor: "#13172A", secondaryColor: "#000000" },
        is_iframe: true,
        // is_iframe: isNative() ? false : true,
        // is_redirection_approach: true,
        Is_redirection_approach: false,
        callback: (r) => {
          console.log('digio callback', digioRef.current)
          if (!r.error_code) {
            handleSuccess();
            return;
          }
          if (r.error_code === "CANCELLED") {
            handleCancel();
            return;
          }
          handleError({ reason: r.message, origin: "initDigio > digio process failed", error: r })
        }
      });

      digioRef.current.init();
      digioRef.current.submit(documentId, identifier, accessToken);
    } catch (error) {
      handleError({ reason: "Something went wrong! Please try again later!", origin: "initDigio > catch block", error })
    }
  }

  const checkKYCStatus = async (checkCount) => {
    try {
      if (checkCount >= 20) {
        const dialogContent = generateSigningStatusContent(VERIFICATION_STATUS.NOT_CONFIRMED);
        setContainerState(prev => ({ 
          ...prev, 
          dialogContent, 
          verificationStatus: VERIFICATION_STATUS.NOT_CONFIRMED,
          isLoading: false 
        }));
        clearInterval(intervalRef.current);
        return;
      }

      const digioStatusResponse = await updateDigioStatus().unwrap();
      console.log('Digio Status Response:', digioStatusResponse);

      const kycResponse = await refetchOnboardingStatus().unwrap();
      console.log('KYC Response:', kycResponse);
      // Check multiple possible paths for kyc.completed
      const isKYCCompleted = 
        kycResponse.data?.data?.kyc?.completed || 
        kycResponse.data?.kyc?.completed || 
        kycResponse.onboardingStatus?.kyc?.completed || 
        false;

      const isInReview =
        kycResponse.data?.data?.kyc?.inReview ||
        kycResponse.data?.kyc?.inReview ||
        kycResponse.onboardingStatus?.kyc?.inReview ||
        false;
      console.log('Is KYC Completed:', isKYCCompleted, isInReview);
      const verificationStatus = isKYCCompleted ? VERIFICATION_STATUS.SUCCEED : isInReview ? VERIFICATION_STATUS.NOT_CONFIRMED : VERIFICATION_STATUS.PENDING;
      const dialogContent = generateSigningStatusContent(verificationStatus);

      if (verificationStatus === VERIFICATION_STATUS.SUCCEED || verificationStatus === VERIFICATION_STATUS.NOT_CONFIRMED) {
        // Show success toast message
        toast.success("KYC process completed successfully!");
        
        setContainerState(prev => ({ 
          ...prev, 
          dialogContent, 
          verificationStatus,
          isLoading: false 
        }));
        clearInterval(intervalRef.current);
      } else {
        // Still pending, keep loading
        setContainerState(prev => ({ 
          ...prev, 
          dialogContent, 
          verificationStatus,
          isLoading: true 
        }));
      }
    } catch (error) {
      handleError({ reason: "Something went wrong! Please try again later!", origin: "checkKYCStatus > catch block", error })
      clearInterval(intervalRef.current);
    }
  }

  const handleSuccess = () => {
    let checkCount = 0;
    const verificationStatus = VERIFICATION_STATUS.PENDING;
    const dialogContent = generateSigningStatusContent(verificationStatus);

    digioRef.current.cancel();
    digioRef.current = null;

    //event-tracker: Initial -> proceed_to_client_agreement_success.
    // tracker.initial.proceedToClientAgreementSuccess();
    
    setContainerState(prev => ({
      ...prev,
      dialogOpen: true,
      dialogContent,
      verificationStatus,
      isLoading: true,
    }));

    // Check immediately first
    checkKYCStatus(checkCount++);

    // Then check every 5 seconds
    intervalRef.current = setInterval(() => {
      checkKYCStatus(checkCount++);
    }, 5000);
  }

  const handleCancel = () => {
    //event-tracker: Initial -> proceed_to_client_agreement_error.
    // tracker.initial.proceedToClientAgreementError({ error_msg: "Process was cancelled" });
    handleError({ reason: "Process was cancelled", verificationStatus: VERIFICATION_STATUS.SUCCEED })
  }

  const handleError = ({ reason, title = "Oops!!!", origin, error, verificationStatus = VERIFICATION_STATUS.FAILED }) => {
    if (origin && error) {
      //event-tracker: Initial -> proceed_to_client_agreement_error.
      // tracker.initial.proceedToClientAgreementError({ error_msg: error.message });
    }
    console.log(`an error occured origin => ${origin}, error =>`, error);

    const dialogContent = {
      title,
      content: reason,
      actionText: "Go Back"
    }

    setContainerState({ ...containerState, dialogContent, verificationStatus, dialogOpen: true, isLoading: false });
  }

  const generateSigningStatusContent = (verificationStatus) => {
    const dialogContent = {
      title: "",
      content: "",
      actionText: "",
    };

    if (verificationStatus === VERIFICATION_STATUS.SUCCEED) {
      dialogContent.title = "Process Completed";
      dialogContent.content = "Congratulations! KYC process is completed successfully!";
      dialogContent.actionText = "Continue";
    } else if (verificationStatus === VERIFICATION_STATUS.FAILED) {
      dialogContent.title = "Process Failed";
      dialogContent.content = "Oops! KYC process failed. Please try again later!";
      dialogContent.actionText = "Go Back";
    } else if (verificationStatus === VERIFICATION_STATUS.NOT_CONFIRMED) {
      dialogContent.title = "In Review";
      dialogContent.content = "Your KYC request is under review. We will update you with status update.";
      dialogContent.actionText = "Go Back";
    } else if (verificationStatus === VERIFICATION_STATUS.PENDING) {
      dialogContent.title = "Process Pending";
      dialogContent.content = "KYC process is not completed yet. Please wait for sometime.";
      dialogContent.actionText = "";
    }

    return dialogContent;
  }

  const handleDialogClose = () => {
    setContainerState(prev => {
      const redirectionURI = REDIRECTION_URIS[prev.verificationStatus];
      navigate(redirectionURI, { replace: true });
      return {
        ...prev,
        dialogOpen: false,
      };
    });
  };

  return (
    <Container sx={{ minHeight: 1, px: { xs: theme.spacing(1.5), sm: 0 } }}>
      <Stack
        direction={{ xs: "row", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{
          width: "100%",
          marginBottom: { xs: theme.spacing(2), sm: theme.spacing(3) },
          gap: { xs: theme.spacing(1.5), sm: 0 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
          {/* Same back arrow as the Bank Account Linking screen */}
          <IconButton
            onClick={() => navigate?.(ROUTES?.ONBOARDING_STATUS)}
            aria-label="back"
            sx={{
              color: "white",
              padding: 0,
              marginRight: 1,
              marginTop: { xs: theme.spacing(0.5), sm: theme.spacing(1) },
              "&:hover": { backgroundColor: "transparent", opacity: 0.8 },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
          <Box>
          <Typography
            sx={{
              fontSize: {
                xs: typography.fontSize.h4,
                sm: typography.fontSize.h3,
              },
              fontWeight: {
                xs: typography.fontFamily.semibold,
                sm: typography.fontWeight.bold,
              },
              color:
                theme.palette.custom?.text?.heading ||
                theme.palette.text.primary,
              marginBottom: { xs: theme.spacing(0.5), sm: theme.spacing(1) },
              lineHeight: { xs: 1.2, sm: 1.3 },
            }}
          >
            KYC Verification
          </Typography>

          <Typography
            sx={{
              fontSize: {
                xs: typography.fontSize.paragraph,
                sm: typography.fontSize.body1,
              },
              fontWeight: typography.fontWeight.regular,
              color:
                theme.palette.custom?.text?.subheading ||
                theme.palette.text.secondary,
              marginBottom: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
            }}
          >
            KYC is mandatory for investing in India.
          </Typography>
          </Box>
        </Box>

      </Stack>
      <div id="response"></div>
      {/* </Stack> */}

      <Typography>Processing KYC...</Typography>

      {containerState.dialogOpen && (
        <>
          <ConfirmationModel
            open={containerState.dialogOpen}
            onClose={handleDialogClose}
            onConfirm={handleDialogClose}
            title={containerState.dialogContent.title}
            description={containerState.dialogContent.content}
            confirmText={containerState.dialogContent.actionText}
            showCancelButton={false}
            loading={containerState.isLoading}
          />
        </>
      )}
    </Container>
  );
};

export default KYCContainer;